import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Comparison_Operators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔍 JavaScript Comparison Operators</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Comparison operators are used to compare two values. They return{" "}
        <code>true</code> or <code>false</code> based on the comparison result.
        These operators are commonly used in conditional statements like{" "}
        <code>if</code>, <code>while</code>, and <code>for</code>.
      </p>

      {/* Key Points Table */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1.1rem",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <thead style={{ backgroundColor: "#e2e8f0" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" }}>Operator</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Description
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Example</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>==</td>
              <td style={{ padding: "10px" }}>Equal to (value only)</td>
              <td style={{ padding: "10px" }}>5 == "5"</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>===</td>
              <td style={{ padding: "10px" }}>Equal to (value and type)</td>
              <td style={{ padding: "10px" }}>5 === "5"</td>
              <td style={{ padding: "10px" }}>false</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>!=</td>
              <td style={{ padding: "10px" }}>Not equal to (value only)</td>
              <td style={{ padding: "10px" }}>5 != "5"</td>
              <td style={{ padding: "10px" }}>false</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>!==</td>
              <td style={{ padding: "10px" }}>Not equal to (value and type)</td>
              <td style={{ padding: "10px" }}>5 !== "5"</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>&gt;</td>
              <td style={{ padding: "10px" }}>Greater than</td>
              <td style={{ padding: "10px" }}>8 &gt; 5</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>&lt;</td>
              <td style={{ padding: "10px" }}>Less than</td>
              <td style={{ padding: "10px" }}>8 &lt; 5</td>
              <td style={{ padding: "10px" }}>false</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>&gt;=</td>
              <td style={{ padding: "10px" }}>Greater than or equal to</td>
              <td style={{ padding: "10px" }}>5 &gt;= 5</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>&lt;=</td>
              <td style={{ padding: "10px" }}>Less than or equal to</td>
              <td style={{ padding: "10px" }}>3 &lt;= 5</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example 1: Equal (==) and Strict Equal (===) */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Equal (==) vs Strict Equal (===)</h2>
        <p>
          <code>==</code> compares values only, while <code>===</code> compares
          both value and type.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(5 == "5");   // true (same value)
console.log(5 === "5");  // false (different type)`}
          />
          <TryItButton
            code={`console.log(5 == "5");   // true (same value)
console.log(5 === "5");  // false (different type)`}
          />
        </div>
      </div>

      {/* Example 2: Not Equal (!=) and Strict Not Equal (!==) */}
      <div className="tutorial-section">
        <h2 className="section-title">
          2️⃣ Not Equal (!=) vs Strict Not Equal (!==)
        </h2>
        <p>
          The <code>!=</code> operator checks inequality of values only, while{" "}
          <code>!==</code> also checks for type differences.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(10 != "10");   // false (same value)
console.log(10 !== "10");  // true (different type)`}
          />
          <TryItButton
            code={`console.log(10 != "10");   // false (same value)
console.log(10 !== "10");  // true (different type)`}
          />
        </div>
      </div>

      {/* Example 3: Greater and Less */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Greater Than &amp; Less Than</h2>
        <p>
          These operators compare numerical values and return <code>true</code>{" "}
          or <code>false</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 8, b = 5;
console.log(a > b);  // true
console.log(a < b);  // false
console.log(a >= 8); // true
console.log(b <= 5); // true`}
          />
          <TryItButton
            code={`let a = 8, b = 5;
console.log(a > b);  // true
console.log(a < b);  // false
console.log(a >= 8); // true
console.log(b <= 5); // true`}
          />
        </div>
      </div>

      {/* Example 4: Real World Example */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Real World Example</h2>
        <p>
          Comparison operators are essential in conditional statements to make
          decisions in code.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let age = 18;
if (age >= 18) {
  console.log("You are eligible to vote!");
} else {
  console.log("You are not eligible yet.");
}`}
          />
          <TryItButton
            code={`let age = 18;
if (age >= 18) {
  console.log("You are eligible to vote!");
} else {
  console.log("You are not eligible yet.");
}`}
          />
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Logical Operators</b> to combine multiple conditions.
          </li>
          <li>
            Explore <b>Ternary Operators</b> for concise condition checks.
          </li>
          <li>
            Practice building <b>if-else</b> and <b>switch</b> programs using
            comparison operators.
          </li>
        </ul>
      </div>
    </div>
  );
}
